/**
 * Pure URL-parameter parsing for `/n2?indicateurId=<id>`
 * (`ADR_N1_WIDGET_NAVIGATION.md` §W6 "Format d'URL", Phase 9C.3B.5 — the
 * technical prerequisite the ADR explicitly deferred).
 *
 * The ADR's traceability note (§W6 "Identifiant transmis") is explicit:
 * `serviceEcosystemiqueId` exists on the `@nbs/reading` TypeScript
 * projection but is **not exposed** by the GraphQL type
 * `N1AggregatedIndicator` — the Cockpit cannot transmit it today, and this
 * parser deliberately has no notion of it: its return type is a single
 * `string | null` for `indicateurId` alone, nothing else.
 */

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Reads `/n2?indicateurId=...` — a single `indicateurId`.
 *
 * - `value === null` (no `indicateurId` param at all, e.g. bare `/n2`) →
 *   `null`.
 * - Defensively decoded (see `parseMesureIdsParam`'s doc comment for why:
 *   `useSearchParams().get(...)` already performs the router's own
 *   decoding; this is a second, no-op-safe pass), then trimmed of
 *   surrounding whitespace.
 * - An empty string, or a whitespace-only value, is rejected and returns
 *   `null` — never an empty-string identifier.
 * - Never accepts more than one identifier: this function's own
 *   signature takes a single `string | null` (the value already produced
 *   by `URLSearchParams#get`, which itself only ever returns the *first*
 *   occurrence of a repeated query key — never `.getAll()`), so multiple
 *   `indicateurId=` occurrences in the URL can never reach this parser as
 *   a list.
 * - Never derives, invents, or falls back to `serviceEcosystemiqueId`.
 */
export function parseIndicateurIdParam(value: string | null): string | null {
  if (value === null) return null;
  const id = safeDecode(value).trim();
  return id === "" ? null : id;
}
